import type { InfiniteData, UseInfiniteQueryOptions } from '@tanstack/react-query';

import { alarmsListHistoricalAlarmActivations } from '@repo/sdk/funcs/alarmsListHistoricalAlarmActivations';
import { ListHistoricalAlarmActivationsResponseBody } from '@repo/sdk/models/components';
import { SDKError } from '@repo/sdk/models/errors';
import { ListHistoricalAlarmActivationsRequest } from '@repo/sdk/models/operations';

import { GaiaClientFunction } from '@/client/types';
import { GaiaQueryWithAgentId } from '@/queries/types';

export interface ListHistoricalAlarmActivationsInfiniteQueryProps extends GaiaQueryWithAgentId {
  initialPageParam?: string;
  client: GaiaClientFunction;
}

export const listHistoricalAlarmActivationsInfiniteQuery = ({
  agentId,
  initialPageParam = '',
  client,
}: ListHistoricalAlarmActivationsInfiniteQueryProps): UseInfiniteQueryOptions<
  ListHistoricalAlarmActivationsResponseBody,
  SDKError,
  InfiniteData<ListHistoricalAlarmActivationsResponseBody, string>,
  (string | undefined)[],
  string
> => {
  return {
    queryKey: [agentId, 'listHistoricalAlarmActivationsInfinite'],
    enabled: !!agentId,
    queryFn: ({
      pageParam,
    }: {
      pageParam: string;
    }): Promise<ListHistoricalAlarmActivationsResponseBody> => {
      return client<
        ListHistoricalAlarmActivationsRequest,
        ListHistoricalAlarmActivationsResponseBody
      >({
        gaiaFunc: alarmsListHistoricalAlarmActivations,
        request: {
          agentId: agentId!,
          cursor: pageParam || initialPageParam,
        },
      });
    },
    initialPageParam: initialPageParam,
    // The historical alarms endpoint returns activations newest-first. To
    // scroll back through older history we follow `previousCursor` — the
    // first page never has a `nextCursor` because there is nothing newer
    // than the most recent activation. Switching to `nextCursor` in PR #910
    // silently broke pagination (hasNextPage was always false), since the
    // API only populates `nextCursor` when paging through older results
    // toward newer ones, which we never do.
    //
    // PR #910 was trying to stop a runaway-request loop where the API kept
    // returning a `previousCursor` past end-of-history, leaving
    // hasNextPage=true forever. We guard against that here by treating "no
    // cursor", "cursor unchanged from the one we just used", or "empty
    // page" as definitive end-of-data signals.
    getNextPageParam: (
      lastPage: ListHistoricalAlarmActivationsResponseBody,
      _allPages: ListHistoricalAlarmActivationsResponseBody[],
      lastPageParam: string
    ): string | undefined => {
      const nextCursor = lastPage.previousCursor;
      if (!nextCursor) return undefined;
      if (nextCursor === lastPageParam) return undefined;
      if (lastPage.values.length === 0) return undefined;
      return nextCursor;
    },
  };
};
